<script setup lang="ts">
import { ref, computed, toRef } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import type { CatalogueItemGroup } from '@/models/Catalogue';
import gettext from '@/i18n/gettext';
import { useCatalogueGroupAutocomplete } from '@/composables/catalogue/useCatalogueGroupAutocomplete';
import GroupAutocomplete from '@/components/catalogue/GroupAutocomplete.vue';
import { getAllCatalogueGroupsFlat } from '@/composables/catalogue/useCatalogueGroupHierarchy';
import CatalogueGroupList from '@/components/catalogue/CatalogueGroupList.vue';
import CatalogueGroupDialog from '@/components/catalogue/CatalogueGroupDialog.vue';

const { $gettext } = gettext;

const props = defineProps<{
  modelValue: boolean;
  groups: CatalogueItemGroup[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'create'): void;
  (e: 'edit', group: CatalogueItemGroup): void;
  (e: 'delete', group: CatalogueItemGroup): void;
  (
    e: 'saveGroup',
    data: {
      groupData: Partial<CatalogueItemGroup>;
      groupToEdit?: CatalogueItemGroup;
    },
  ): void;
}>();

const groupSearchText = ref('');

// Use the group autocomplete composable
const { autocompleteOptions, getFilteredGroups } =
  useCatalogueGroupAutocomplete(toRef(props, 'groups'));

const filteredGroups = computed(() => {
  return getFilteredGroups(groupSearchText.value);
});

const showGroupDialog = ref(false);
const groupToEdit = ref<CatalogueItemGroup | undefined>(undefined);

const handleCreate = () => {
  groupToEdit.value = undefined;
  showGroupDialog.value = true;
};

const handleEdit = (group: CatalogueItemGroup) => {
  groupToEdit.value = group;
  showGroupDialog.value = true;
};

const handleSaveGroup = (groupData: Partial<CatalogueItemGroup>) => {
  emit('saveGroup', { groupData, groupToEdit: groupToEdit.value });
  showGroupDialog.value = false;
};

const handleGroupSelect = (selectedGroup: string | null) => {
  const option = autocompleteOptions.value.find(
    (opt) => opt.value === selectedGroup,
  );
  if (option) {
    groupSearchText.value = option.path;
  }
};
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="800px"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="pa-6 bg-white" style="border-radius: 8px">
      <div class="d-flex align-center mb-4">
        <h2 class="text-h5">{{ $gettext('Manage Groups') }}</h2>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          @click="$emit('update:modelValue', false)"
        />
      </div>

      <div>
        <div class="d-flex align-center mb-4">
          <GroupAutocomplete
            v-model="groupSearchText"
            :groups="props.groups"
            :label="$gettext('Search Groups')"
            clearable
            hide-details
            class="me-4"
            style="min-width: 300px"
            @update:model-value="handleGroupSelect"
          />
          <r-btn variant="outlined" icon="mdi-plus" @click="handleCreate">
            {{ $gettext('Add Group') }}
          </r-btn>
        </div>
        <CatalogueGroupList
          :groups="filteredGroups"
          :all-groups="getAllCatalogueGroupsFlat(props.groups)"
          :expand-all="(groupSearchText || '').length > 0"
          @edit="handleEdit"
          @delete="(group) => emit('delete', group)"
        />
      </div>

      <div class="d-flex justify-end mt-6">
        <v-btn variant="text" @click="$emit('update:modelValue', false)">
          {{ $gettext('Close') }}
        </v-btn>
      </div>

      <CatalogueGroupDialog
        v-model="showGroupDialog"
        :group="groupToEdit"
        :groups="getAllCatalogueGroupsFlat(props.groups)"
        @save="handleSaveGroup"
      />
    </div>
  </v-dialog>
</template>
